import { api } from './api';

// ==================== DTOs ====================

export interface CircleDto {
  id: string;
  name: string;
  description: string;
  creatorId: string;
  discipline: string;
  privacy: string;
  maxMembers: number;
  memberCount: number;
  lastActivityAt: string;
  isActive: boolean;
  createdAt: string;
}

export interface CircleDetailDto extends CircleDto {
  members: CircleMemberDto[];
}

export interface CircleMemberDto {
  id: string;
  circleId: string;
  userId: string;
  role: string;
  joinedAt: string;
  status: string;
  lastActiveAt: string;
}

export interface CirclePostDto {
  id: string;
  circleId: string;
  authorId: string;
  content: string;
  postType: string;
  mediaUrls: string[];
  likeCount: number;
  commentCount: number;
  createdAt: string;
  comments: CircleCommentDto[];
}

export interface CircleCommentDto {
  id: string;
  postId: string;
  authorId: string;
  content: string;
  createdAt: string;
}

// ==================== Request Types ====================

export interface CreateCircleRequest {
  name: string;
  description: string;
  discipline: string;
  privacy: string;
  maxMembers?: number;
}

export interface UpdateCircleRequest {
  name?: string;
  description?: string;
}

export interface CreateCirclePostRequest {
  content: string;
  postType: string;
  mediaUrls?: string[];
}

export interface CreateCommentRequest {
  content: string;
}

// ==================== Service ====================

export const circlesService = {
  // Circles
  async getUserCircles(): Promise<CircleDto[]> {
    return api.get<CircleDto[]>('/circles');
  },

  async createCircle(request: CreateCircleRequest): Promise<CircleDto> {
    return api.post<CircleDto>('/circles', request);
  },

  async discoverCircles(discipline?: string): Promise<CircleDto[]> {
    const params = discipline ? `?discipline=${encodeURIComponent(discipline)}` : '';
    return api.get<CircleDto[]>(`/circles/discover${params}`);
  },

  async getCircle(id: string): Promise<CircleDetailDto | null> {
    try {
      return await api.get<CircleDetailDto>(`/circles/${id}`);
    } catch {
      return null;
    }
  },

  async updateCircle(id: string, request: UpdateCircleRequest): Promise<CircleDto> {
    return api.put<CircleDto>(`/circles/${id}`, request);
  },

  async deleteCircle(id: string): Promise<void> {
    await api.delete(`/circles/${id}`);
  },

  // Membership
  async joinCircle(id: string): Promise<CircleMemberDto> {
    return api.post<CircleMemberDto>(`/circles/${id}/join`, {});
  },

  async leaveCircle(id: string): Promise<void> {
    await api.post(`/circles/${id}/leave`, {});
  },

  async getMembers(id: string): Promise<CircleMemberDto[]> {
    return api.get<CircleMemberDto[]>(`/circles/${id}/members`);
  },

  // Posts
  async getFeed(circleId: string, pageNumber = 1, pageSize = 20): Promise<CirclePostDto[]> {
    return api.get<CirclePostDto[]>(`/circles/${circleId}/posts?pageNumber=${pageNumber}&pageSize=${pageSize}`);
  },

  async createPost(circleId: string, request: CreateCirclePostRequest): Promise<CirclePostDto> {
    return api.post<CirclePostDto>(`/circles/${circleId}/posts`, request);
  },

  async likePost(circleId: string, postId: string): Promise<{ liked: boolean }> {
    return api.post<{ liked: boolean }>(`/circles/${circleId}/posts/${postId}/like`, {});
  },

  async createComment(circleId: string, postId: string, request: CreateCommentRequest): Promise<CircleCommentDto> {
    return api.post<CircleCommentDto>(`/circles/${circleId}/posts/${postId}/comments`, request);
  },
};
